import React, { useState, useEffect } from 'react';
import ImageUpload from './ImageUpload';
import { motion, AnimatePresence } from 'framer-motion';
import { Image as ImageIcon, Video, Plus, Trash2, Play, X, Edit3 } from 'lucide-react';
import { db } from '../firebase';
import { doc, setDoc, onSnapshot, collection, deleteDoc } from 'firebase/firestore';
import Swal from 'sweetalert2';

const getYoutubeThumbnail = (url) => {
  if (!url) return '';
  let videoId = '';
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  if (match && match[2].length === 11) {
    videoId = match[2];
  } else {
    const parts = url.split('/');
    const lastPart = parts[parts.length - 1];
    if (lastPart && lastPart.length === 11) {
      videoId = lastPart;
    }
  }
  return videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : '';
};

const cleanTitle = (title, type) => {
  if (!title) return '';
  let cleaned = title;
  if (type === 'image') {
    cleaned = cleaned.replace(/factory\s+process\s*[-|:]?\s*/i, '');
  } else if (type === 'video') {
    cleaned = cleaned.replace(/corporate\s+broadcast\s*[-|:]?\s*/i, '');
  }
  return cleaned.trim();
};

const AdminGallery = () => {
  const [gallery, setGallery] = useState({ images: [], videos: [] });
  const [activeTab, setActiveTab] = useState('photos'); // 'photos' | 'videos'
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null); // null or { type: 'images' | 'videos', id: string, title: string, url: string }
  const [newImage, setNewImage] = useState({ title: '', url: '' });
  const [newVideo, setNewVideo] = useState({ title: '', url: '' });

  useEffect(() => {
    let docGallery = { images: [], videos: [] };
    let colImages = [];
    let colVideos = [];

    const updateState = () => {
      const allImages = [
        ...(docGallery.images || []).map(img => ({ ...img, isCollection: false })),
        ...colImages.map(img => ({ ...img, isCollection: true }))
      ];
      const allVideos = [
        ...(docGallery.videos || []).map(vid => ({ ...vid, isCollection: false })),
        ...colVideos.map(vid => ({ ...vid, isCollection: true }))
      ];

      const uniqueImages = Array.from(new Map(allImages.map(i => [String(i.id), i])).values());
      const uniqueVideos = Array.from(new Map(allVideos.map(v => [String(v.id), v])).values());

      setGallery({ images: uniqueImages, videos: uniqueVideos });
    };

    const unsubDoc = onSnapshot(doc(db, 'site_content', 'gallery'), (d) => {
      if (d.exists()) docGallery = d.data();
      else docGallery = { images: [], videos: [] };
      updateState();
    });

    const unsubImg = onSnapshot(collection(db, 'gallery_images'), (snapshot) => {
      colImages = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      updateState();
    });

    const unsubVid = onSnapshot(collection(db, 'gallery_videos'), (snapshot) => {
      colVideos = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      updateState();
    });

    return () => {
      unsubDoc();
      unsubImg();
      unsubVid();
    };
  }, []);

  useEffect(() => {
    if (isAddModalOpen || editingItem) {
      document.body.classList.add('admin-modal-open');
    } else {
      document.body.classList.remove('admin-modal-open');
    }
    return () => document.body.classList.remove('admin-modal-open');
  }, [isAddModalOpen, editingItem]);

  const addImage = async (e) => {
    e.preventDefault();
    if (!newImage.title || !newImage.url) {
      Swal.fire('Warning', 'Please provide both photo title and select an image', 'warning');
      return;
    }
    try {
      const newDocRef = doc(collection(db, 'gallery_images'));
      await setDoc(newDocRef, {
        title: newImage.title,
        url: newImage.url,
        createdAt: Date.now()
      });
      setNewImage({ title: '', url: '' });
      setIsAddModalOpen(false);
      Swal.fire({
        title: 'Success',
        text: 'Image added to gallery successfully',
        icon: 'success',
        confirmButtonColor: '#2E7D32',
      });
    } catch (error) {
      console.error('Error adding image to gallery:', error);
      Swal.fire('Error', error.message || 'Failed to add image', 'error');
    }
  };

  const addVideo = async (e) => {
    e.preventDefault();
    if (!newVideo.title || !newVideo.url) return;
    let embedUrl = newVideo.url;
    if (embedUrl.includes('watch?v=')) {
      embedUrl = embedUrl.replace('watch?v=', 'embed/');
    } else if (embedUrl.includes('youtu.be/')) {
      embedUrl = embedUrl.replace('youtu.be/', 'www.youtube.com/embed/');
    }

    try {
      const newDocRef = doc(collection(db, 'gallery_videos'));
      await setDoc(newDocRef, {
        title: newVideo.title,
        url: embedUrl,
        createdAt: Date.now()
      });
      setNewVideo({ title: '', url: '' });
      setIsAddModalOpen(false);
      Swal.fire({
        title: 'Success',
        text: 'Video added to gallery successfully',
        icon: 'success',
        confirmButtonColor: '#2E7D32',
      });
    } catch (error) {
      console.error('Error adding video to gallery:', error);
      Swal.fire('Error', error.message || 'Failed to add video', 'error');
    }
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    const type = editingItem.type; // 'images' or 'videos'
    const colName = type === 'images' ? 'gallery_images' : 'gallery_videos';
    let finalUrl = editingItem.url;
    if (type === 'videos') {
      if (finalUrl.includes('watch?v=')) {
        finalUrl = finalUrl.replace('watch?v=', 'embed/');
      } else if (finalUrl.includes('youtu.be/')) {
        finalUrl = finalUrl.replace('youtu.be/', 'www.youtube.com/embed/');
      }
    }

    try {
      if (editingItem.isCollection) {
        await setDoc(doc(db, colName, String(editingItem.id)), {
          title: editingItem.title,
          url: finalUrl
        }, { merge: true });
      } else {
        const updatedItems = (gallery[type] || []).map(item => {
          if (String(item.id) === String(editingItem.id)) {
            return { ...item, title: editingItem.title, url: finalUrl };
          }
          return item;
        });
        const nonColImages = (type === 'images' ? updatedItems : gallery.images).filter(i => !i.isCollection);
        const nonColVideos = (type === 'videos' ? updatedItems : gallery.videos).filter(v => !v.isCollection);
        await setDoc(doc(db, 'site_content', 'gallery'), {
          images: nonColImages,
          videos: nonColVideos
        });
      }
      setEditingItem(null);
      Swal.fire({
        title: 'Success',
        text: 'Media item updated successfully',
        icon: 'success',
        confirmButtonColor: '#2E7D32',
      });
    } catch (error) {
      console.error('Error updating item:', error);
      Swal.fire('Error', error.message || 'Failed to update item', 'error');
    }
  };

  const removeItem = async (type, id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "Delete this item from gallery?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
    });

    if (result.isConfirmed) {
      const itemToDelete = (gallery[type] || []).find(i => String(i.id) === String(id));
      const colName = type === 'images' ? 'gallery_images' : 'gallery_videos';
      try {
        if (itemToDelete && itemToDelete.isCollection) {
          await deleteDoc(doc(db, colName, String(id)));
        } else {
          const remainingNonColImages = gallery.images.filter(i => String(i.id) !== String(id) && !i.isCollection);
          const remainingNonColVideos = gallery.videos.filter(v => String(v.id) !== String(id) && !v.isCollection);
          await setDoc(doc(db, 'site_content', 'gallery'), {
            images: remainingNonColImages,
            videos: remainingNonColVideos
          });
        }
        Swal.fire('Deleted!', 'Item removed.', 'success');
      } catch (error) {
        console.error('Error removing item:', error);
        Swal.fire('Error', error.message || 'Failed to remove item', 'error');
      }
    }
  };

  return (
    <div className="space-y-8 pb-20 font-poppins">

      {/* Header Banner */}
      <div className="bg-sugarcane-charcoal p-6 rounded-2xl text-white flex flex-col md:flex-row justify-between items-center gap-6 relative overflow-hidden shadow-md">
        <div className="absolute top-0 left-0 w-64 h-64 bg-sugarcane-green/10 rounded-full -ml-32 -mt-32 blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-emerald-700/10 rounded-full -mr-32 -mb-32 blur-3xl"></div>

        <div className="relative z-10">
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-10 h-10 bg-sugarcane-green rounded-xl flex items-center justify-center text-white">
              <ImageIcon size={20} />
            </div>
            <h2 className="text-2xl font-bold tracking-tight">Gallery Media Management</h2>
          </div>
          <p className="text-gray-400 text-sm italic">Manage organization photograph reels, factory tours, and cane development videos.</p>
        </div>

        <div className="relative z-10 flex gap-4">
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="bg-sugarcane-green hover:bg-white hover:text-sugarcane-charcoal text-white px-5 py-2.5 rounded-xl text-sm font-semibold flex items-center space-x-2 transition-all shadow-md cursor-pointer"
          >
            <Plus size={18} />
            <span>{activeTab === 'photos' ? 'Add Photo' : 'Add Video'}</span>
          </button>
        </div>
      </div>

      {/* Horizontal Category Switcher */}
      <div className="flex bg-gray-100/80 p-1 rounded-2xl max-w-xs mx-auto border border-gray-200/40 shadow-inner backdrop-blur-md">
        {[
          { key: 'photos', label: 'Photos', icon: ImageIcon },
          { key: 'videos', label: 'Videos', icon: Video }
        ].map((tab) => {
          const isActive = activeTab === tab.key;
          const count = tab.key === 'photos' ? gallery.images?.length || 0 : gallery.videos?.length || 0;
          const Icon = tab.icon;

          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-1 py-2.5 rounded-xl font-bold uppercase text-[10px] tracking-wider transition-all duration-300 flex items-center justify-center space-x-2 cursor-pointer ${isActive
                ? 'bg-sugarcane-green text-white shadow-md shadow-sugarcane-green/10'
                : 'text-gray-400 hover:text-sugarcane-charcoal'
                }`}
            >
              <Icon size={12} />
              <span>{tab.label}</span>
              <span className={`inline-flex items-center justify-center px-2 py-0.5 rounded-full text-[8px] font-black tracking-normal ${isActive ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-400'
                }`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Media Displays */}
      {activeTab === 'photos' ? (
        // Photo gallery layout
        (!gallery.images || gallery.images.length === 0) ? (
          <div className="bg-white py-20 text-center max-w-sm mx-auto rounded-2xl shadow-sm border border-gray-100">
            <div className="bg-gray-50 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-gray-100 shadow-inner">
              <ImageIcon className="text-gray-300" size={28} />
            </div>
            <h4 className="text-md font-bold text-sugarcane-charcoal mb-1">No Photos Uploaded</h4>
            <p className="text-xs text-gray-400 leading-relaxed px-6">
              There are currently no photos in the gallery. Use the button above to upload a photo.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            <AnimatePresence mode='popLayout'>
              {gallery.images.map((img) => {
                const displayTitle = cleanTitle(img.title, 'image');
                return (
                  <motion.div
                    key={img.id}
                    layout
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.3 }}
                    className="group relative aspect-square rounded-2xl overflow-hidden shadow-sm border border-gray-100 bg-white p-2"
                  >
                    <div className="rounded-xl overflow-hidden h-full w-full relative bg-gray-50">
                      <img src={img.url} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out" alt={displayTitle} />

                      {/* Top-right action controls (matching video layout) */}
                      <div className="absolute top-4 right-4 z-20 flex space-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingItem({ type: 'images', id: img.id, title: img.title, url: img.url });
                          }}
                          className="p-2 bg-white/90 text-blue-600 hover:bg-blue-600 hover:text-white rounded-lg transition-all shadow-sm flex items-center justify-center border border-gray-100 cursor-pointer"
                        >
                          <Edit3 size={14} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeItem('images', img.id);
                          }}
                          className="p-2 bg-white/90 text-red-600 hover:bg-red-600 hover:text-white rounded-lg transition-all shadow-sm flex items-center justify-center border border-gray-100 cursor-pointer"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>

                      {/* Text overlay on hover */}
                      <div className="absolute inset-0 bg-sugarcane-charcoal/70 opacity-0 group-hover:opacity-100 transition-all duration-300 flex flex-col items-center justify-center p-4 text-center z-10">
                        <p className="text-white text-xs font-bold px-2 line-clamp-2">{displayTitle}</p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )
      ) : (
        // Video gallery layout
        (!gallery.videos || gallery.videos.length === 0) ? (
          <div className="bg-white py-20 text-center max-w-sm mx-auto rounded-2xl shadow-sm border border-gray-100">
            <div className="bg-gray-50 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-gray-100 shadow-inner">
              <Video className="text-gray-300" size={28} />
            </div>
            <h4 className="text-md font-bold text-sugarcane-charcoal mb-1">No Videos Added</h4>
            <p className="text-xs text-gray-400 leading-relaxed px-6">
              There are currently no videos in the gallery. Use the button above to add a video.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <AnimatePresence mode='popLayout'>
              {gallery.videos.map((vid) => {
                const displayTitle = cleanTitle(vid.title, 'video');
                return (
                  <motion.div
                    key={vid.id}
                    layout
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.3 }}
                    className="bg-white rounded-2xl overflow-hidden border border-gray-100 group shadow-sm hover:shadow-md hover:border-sugarcane-green/20 transition-all duration-300 flex flex-col justify-between"
                  >
                    <div>
                      <div className="relative aspect-video bg-sugarcane-charcoal">
                        <img
                          src={getYoutubeThumbnail(vid.url) || 'https://via.placeholder.com/300x180'}
                          alt={displayTitle}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/10 group-hover:bg-black/30 transition-colors duration-300">
                          <Play size={40} className="text-white fill-white/80 opacity-90 group-hover:scale-110 transition-transform duration-300" />
                        </div>
                        <div className="absolute top-4 right-4 z-10 flex space-x-2">
                          <button
                            onClick={() => setEditingItem({ type: 'videos', id: vid.id, title: vid.title, url: vid.url })}
                            className="p-2 bg-white/90 text-blue-600 hover:bg-blue-600 hover:text-white rounded-lg transition-all shadow-sm flex items-center justify-center border border-gray-100 cursor-pointer"
                          >
                            <Edit3 size={14} />
                          </button>
                          <button
                            onClick={() => removeItem('videos', vid.id)}
                            className="p-2 bg-white/90 text-red-600 hover:bg-red-600 hover:text-white rounded-lg transition-all shadow-sm flex items-center justify-center border border-gray-100 cursor-pointer"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                      <div className="p-5 text-center">
                        <h4 className="font-bold text-sm text-sugarcane-charcoal line-clamp-2 px-2 leading-snug">{displayTitle}</h4>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )
      )}

      {/* Unified Form Modal (Add & Edit) */}
      <AnimatePresence>
        {(isAddModalOpen || editingItem) && (
          <div className="fixed inset-0 z-[1050] flex items-center justify-center p-4 bg-sugarcane-charcoal/80 backdrop-blur-md admin-modal-overlay">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-2xl w-full max-w-xl shadow-2xl relative max-h-[90vh] overflow-y-auto border border-gray-100"
            >
              {/* Modal Header */}
              <div className="sticky top-0 bg-white/95 backdrop-blur-md px-6 py-5 border-b border-gray-100 flex justify-between items-center z-10">
                <div>
                  <span className="px-3 py-1 bg-sugarcane-green/5 rounded-full text-[9px] font-bold uppercase tracking-widest text-sugarcane-green border border-sugarcane-green/10">
                    Gallery Asset
                  </span>
                  <h3 className="text-xl font-bold text-sugarcane-charcoal mt-1.5">
                    {editingItem
                      ? (editingItem.type === 'images' ? 'Edit Photo Details' : 'Edit Video Details')
                      : (activeTab === 'photos' ? 'Add Photo to Gallery' : 'Add Video to Gallery')
                    }
                  </h3>
                </div>
                <button
                  onClick={() => {
                    setIsAddModalOpen(false);
                    setEditingItem(null);
                  }}
                  className="p-2 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors cursor-pointer"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Form Body */}
              <form
                onSubmit={editingItem ? handleEditSave : (activeTab === 'photos' ? addImage : addVideo)}
                className="p-6 space-y-6"
              >
                {/* Form fields */}
                {((editingItem && editingItem.type === 'images') || (!editingItem && activeTab === 'photos')) ? (
                  // PHOTO FORM (Add/Edit)
                  <div className="space-y-5">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">Photo Title</label>
                      <input
                        type="text"
                        required
                        value={editingItem ? editingItem.title : newImage.title}
                        onChange={(e) => {
                          if (editingItem) {
                            setEditingItem({ ...editingItem, title: e.target.value });
                          } else {
                            setNewImage({ ...newImage, title: e.target.value });
                          }
                        }}
                        placeholder="e.g. Factory Main Entrance"
                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm font-semibold focus:bg-white focus:border-sugarcane-green focus:ring-2 focus:ring-sugarcane-green/10 transition-all outline-none text-sugarcane-charcoal shadow-inner"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">Upload Visual Asset</label>
                      <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6 flex flex-col items-center justify-center shadow-sm">
                        <div className="w-full max-w-xs overflow-hidden rounded-xl border border-gray-200 bg-white">
                          <ImageUpload
                            folder={editingItem ? "gallery-edit" : "gallery-add"}
                            initialImage={editingItem ? editingItem.url : newImage.url}
                            onUploadSuccess={(url) => {
                              if (editingItem) {
                                setEditingItem({ ...editingItem, url });
                              } else {
                                setNewImage({ ...newImage, url });
                              }
                            }}
                          />
                        </div>
                        <p className="text-[10px] text-gray-400 mt-3 font-medium">Recommended 16:9 or 1:1 format, max 1MB</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  // VIDEO FORM (Add/Edit)
                  <div className="space-y-5">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">Video Title</label>
                      <input
                        type="text"
                        required
                        value={editingItem ? editingItem.title : newVideo.title}
                        onChange={(e) => {
                          if (editingItem) {
                            setEditingItem({ ...editingItem, title: e.target.value });
                          } else {
                            setNewVideo({ ...newVideo, title: e.target.value });
                          }
                        }}
                        placeholder="e.g. Factory Tour"
                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm font-semibold focus:bg-white focus:border-sugarcane-green focus:ring-2 focus:ring-sugarcane-green/10 transition-all duration-200 outline-none text-sugarcane-charcoal shadow-inner"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">YouTube URL</label>
                      <input
                        type="url"
                        required
                        value={editingItem ? editingItem.url : newVideo.url}
                        onChange={(e) => {
                          if (editingItem) {
                            setEditingItem({ ...editingItem, url: e.target.value });
                          } else {
                            setNewVideo({ ...newVideo, url: e.target.value });
                          }
                        }}
                        placeholder="https://www.youtube.com/watch?v=..."
                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm font-semibold focus:bg-white focus:border-sugarcane-green focus:ring-2 focus:ring-sugarcane-green/10 transition-all duration-200 outline-none text-sugarcane-charcoal shadow-inner"
                      />
                    </div>
                  </div>
                )}

                {/* Form Actions Footer */}
                <div className="flex justify-end gap-3 pt-6 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => {
                      setIsAddModalOpen(false);
                      setEditingItem(null);
                    }}
                    className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-xl font-bold text-sm transition-all cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={
                      editingItem
                        ? (!editingItem.url || !editingItem.title)
                        : (activeTab === 'photos'
                          ? (!newImage.url || !newImage.title)
                          : (!newVideo.url || !newVideo.title)
                        )
                    }
                    className="px-6 py-2.5 bg-sugarcane-green hover:bg-sugarcane-charcoal text-white rounded-xl font-bold text-sm transition-all shadow-md shadow-sugarcane-green/10 disabled:opacity-50 flex items-center gap-2 cursor-pointer"
                  >
                    <Plus size={16} />
                    <span>{editingItem ? 'Save Changes' : (activeTab === 'photos' ? 'Add Photo' : 'Add Video')}</span>
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default AdminGallery;
